import errorProcessor from '@frontend/ll-fe-tracker/errorProcessor'
import {
  GET_ORGANISATION_TITLES,
  GET_ORGANISATION_KEYWORDS,
  GET_ORGANISATION_FEATURES,
} from '../graphql/queries/organisation'

const actionTypes = {
  FETCH_ORG_TITLES: 'FETCH_ORG_TITLES',
  FETCH_ORG_KEYWORDS: 'FETCH_ORG_KEYWORDS',
  FETCH_ORG_FEATURES: 'FETCH_ORG_FEATURES',
}

const mutationTypes = {
  SET_ORGANISATION_TITLES: 'SET_ORGANISATION_TITLES',
  SET_ORGANISATION_TITLES_LOADING: 'SET_ORGANISATION_TITLES_LOADING',
  SET_ORGANISATION_KEYWORDS: 'SET_ORGANISATION_KEYWORDS',
  SET_ORGANISATION_KEYWORDS_LOADING: 'SET_ORGANISATION_KEYWORDS_LOADING',
  SET_ORGANISATION_FEATURES: 'SET_ORGANISATION_FEATURES',
  SET_ORGANISATION_FEATURES_LOADING: 'SET_ORGANISATION_FEATURES_LOADING',
}

export const ORG_KEYWORDS = {
  designation: {
    key: 'designation',
    default: 'Designation',
  },
  all_designations: {
    key: 'all_designations',
    default: 'x-designations',
  },
  designation_filter: {
    key: 'filter_designations',
    default: 'x-designations',
  },
  org_internal_title: {
    key: 'org_internal_title',
    default: 'Internal Title',
  },
  all_org_internal_title: {
    key: 'org_internal_title',
    default: 'All title',
  },
  org_internal_title_filter_label: {
    key: 'org_internal_title_filter_label',
    default: 'x-title',
  },
  evidence_comment: {
    key: 'evidence_comment',
    default: 'Comment',
  },
  perf_v2_invalid_section_total_weightage: {
    key: 'perf_v2_invalid_section_total_weightage',
    default: 'The total weightage in sections is not equal 100%. Please check again.',
  },
  perf_v2_invalid_sub_section_total_weightage: {
    key: 'perf_v2_invalid_sub_section_total_weightage',
    default: 'The total weightage in sub sections is not equal 100%. Please check again.',
  },
  modal_confirm_button: {
    key: 'modal_confirm_button',
    default: 'Confirm',
  },
  reporting_to_officer: {
    key: 'reporting_to_officer',
    default: 'reporting-to-officer',
  },
  reporting_to: {
    key: 'reporting_to',
    default: 'reporting-to',
  },
}

export const state = () => ({
  titlesByOrg: {},
  titlesByCategory: {},
  titlesLoading: false,
  keywordsByOrg: {},
  keywordsLoading: false,
  featuresByOrg: {},
  featuresLoading: false,
})

const getTitleCategoryByCountry = (country) => {
  switch (country) {
    // TODO: Consider to bring this to BE to manage
    case 'SG': {
      return 'SG_ECDA'
    }
    default: {
      return 'SG_ECDA'
    }
  }
}

export const mutations = {
  [mutationTypes.SET_ORGANISATION_TITLES](state, { organisationId, category, orgTitles }) {
    if (organisationId && organisationId !== 'undefined') {
      state.titlesByOrg = {
        ...state.titlesByOrg,
        [organisationId]: (orgTitles ?? []).map((titleItem) => ({
          value: titleItem.id,
          label: titleItem.title,
          title: titleItem.title,
        })),
      }
    } else if (category) {
      state.titlesByCategory = {
        ...state.titlesByCategory,
        [category]: (orgTitles ?? []).map((titleItem) => ({
          value: titleItem.id,
          label: titleItem.title,
          title: titleItem.title,
        })),
      }
    }
  },
  [mutationTypes.SET_ORGANISATION_TITLES_LOADING](state, loadingState) {
    state.keywordsLoading = loadingState
  },
  [mutationTypes.SET_ORGANISATION_KEYWORDS](state, { organisationId, orgKeywords }) {
    const keywordsByKey = orgKeywords.reduce((acc, keyword) => {
      acc[keyword.key] = keyword.value
      return acc
    }, {})
    state.keywordsByOrg[organisationId] = keywordsByKey ?? {}
  },
  [mutationTypes.SET_ORGANISATION_KEYWORDS_LOADING](state, loadingState) {
    state.keywordsLoading = loadingState
  },
  [mutationTypes.SET_ORGANISATION_FEATURES](state, { organisationId, orgFeatures }) {
    const featuresByKey = orgFeatures.reduce((acc, feature) => {
      acc[feature.key] = feature.status
      return acc
    }, {})
    state.featuresByOrg = {
      ...state.featuresByOrg,
      [organisationId]: featuresByKey || state.featuresByOrg[organisationId] || {},
    }
  },
  [mutationTypes.SET_ORGANISATION_FEATURES_LOADING](state, loadingState) {
    state.featuresLoading = loadingState
  },
}

export const actions = {
  async [actionTypes.FETCH_ORG_TITLES]({ commit, state }, { organisationId, country } = {}) {
    try {
      if (!organisationId && !country) {
        errorProcessor.capture(new Error('No organisationId or category provided'))
        return Promise.resolve([])
      }
      if (state.titlesByOrg[organisationId]) {
        return Promise.resolve(true)
      }

      const params = {}

      if (country) params.category = getTitleCategoryByCountry(country)
      if (organisationId) params.organisationId = `${organisationId}`

      commit(mutationTypes.SET_ORGANISATION_TITLES_LOADING, true)
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_ORGANISATION_TITLES,
        variables: params,
      })
      if (data.organisationTitles) {
        commit(mutationTypes.SET_ORGANISATION_TITLES, {
          organisationId: `${organisationId}`,
          category: params.category,
          orgTitles: data.organisationTitles,
        })
      }
      commit(mutationTypes.SET_ORGANISATION_TITLES_LOADING, false)
    } catch (error) {
      commit(mutationTypes.SET_ORGANISATION_TITLES_LOADING, false)
    }
  },
  async [actionTypes.FETCH_ORG_KEYWORDS]({ commit, state }, { organisationId } = {}) {
    try {
      if (!organisationId) {
        errorProcessor.capture(new Error('No organisationId provided'))
        return Promise.resolve([])
      }
      if (state.keywordsByOrg[organisationId]) {
        return Promise.resolve(true)
      }

      commit(mutationTypes.SET_ORGANISATION_KEYWORDS_LOADING, true)
      const client = this.app.apolloProvider.defaultClient
      const response = await client.query({
        query: GET_ORGANISATION_KEYWORDS,
        variables: { organisationId },
      })
      const { data } = response

      if (data.organisationKeywords) {
        commit(mutationTypes.SET_ORGANISATION_KEYWORDS, {
          organisationId: `${organisationId}`,
          orgKeywords: data.organisationKeywords,
        })
      }
      commit(mutationTypes.SET_ORGANISATION_KEYWORDS_LOADING, false)
    } catch (error) {
      commit(mutationTypes.SET_ORGANISATION_KEYWORDS_LOADING, false)
    }
  },
  async [actionTypes.FETCH_ORG_FEATURES]({ commit, state }, { organisationId } = {}) {
    try {
      if (!organisationId) {
        errorProcessor.capture(new Error('No organisationId provided'))
        return Promise.resolve([])
      }
      if (state.featuresByOrg[organisationId]) {
        return Promise.resolve(true)
      }

      commit(mutationTypes.SET_ORGANISATION_FEATURES_LOADING, true)
      const client = this.app.apolloProvider.defaultClient
      const response = await client.query({
        query: GET_ORGANISATION_FEATURES,
        variables: { organisationId },
      })
      const { data } = response
      if (data.organisationFeatures) {
        commit(mutationTypes.SET_ORGANISATION_FEATURES, {
          organisationId: `${organisationId}`,
          orgFeatures: data.organisationFeatures,
        })
      }
      commit(mutationTypes.SET_ORGANISATION_FEATURES_LOADING, false)
    } catch (error) {
      commit(mutationTypes.SET_ORGANISATION_FEATURES_LOADING, false)
    }
  },
}

export const getters = {
  titlesByOrg: (state) => (organisationId) => state.titlesByOrg[`${organisationId}`] ?? [],
  titlesByCategory:
    (state) =>
    ({ country, category }) => {
      const validCategory = category ?? getTitleCategoryByCountry(country)
      return state.titlesByCategory ? state.titlesByCategory[validCategory] || [] : []
    },
  titlesLoading: (state) => state.titlesLoading,
  keywordsByOrg: (state) => (organisationId) => state.keywordsByOrg[`${organisationId}`] ?? {},
  keywordsLoading: (state) => state.keywordsLoading,
  featuresByOrg: (state) => (organisationId) => state.featuresByOrg[`${organisationId}`] ?? {},
  featuresLoading: (state) => state.keywordsLoading,
}
